import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
import {setupMotion, revealElements} from '../lib/reduced-motion.js';

const isInView = (element) => {
  const position = element.getBoundingClientRect();
  return (
    (position.top >= 0 && position.bottom <= window.innerHeight) ||
    (position.top < window.innerHeight && position.bottom >= 0)
  );
};

class SpecialFeatureA {
  constructor() {
    this.getCurrentSection = this.getCurrentSection.bind(this);
    this.updateIndex = this.updateIndex.bind(this);
    this.maxLength = 0;
    this.currentIndex = 1;
    this.nextIndex = 2;
    this.previousIndex = 1;
    this.lastContent = null;
  }

  init() {
    setupMotion(gsap, {
      reduced: () => {
        const scope = document.querySelector('[data-module="features-special-a"]');
        if (!scope) return;
        // No ScrollTrigger or pinning; ensure everything is visible and shapes are at rest.
        revealElements(gsap, scope, '[data-element]');
        gsap.set('.shape__special--1', {translateY: 0});
        gsap.set('.shape__special--2', {translateX: 0});
      },
      full: () => {
        gsap.registerPlugin(ScrollTrigger);
        gsap.defaults({overwrite: 'auto'});

        // ScrollTrigger for the whole feature (pin the images)
        const ST = ScrollTrigger.create({
          trigger: '[data-module="features-special-a"]',
          start: 'top top',
          end: 'bottom bottom',
          onUpdate: this.getCurrentSection,
          pin: '[data-element="features-special-a-images"]',
          pinSpacing: false,
          invalidateOnRefresh: true,
        });

        // Enable/disable for responsive breakpoint
        const media = window.matchMedia('screen and (max-width: 800px)');

        /**
         * Instantiate based on media query
         */
        function checkSTState() {
          if (media.matches) {
            ST.disable();
          } else {
            ST.enable();
          }
        }
        ScrollTrigger.addEventListener('refreshInit', checkSTState);
        checkSTState();

        // Original content behavior setup
        const contentMarkers = gsap.utils.toArray('[data-element="content-marker"]');
        this.setIndex(contentMarkers);
        this.maxLength = contentMarkers.length;

        contentMarkers.forEach((marker) => {
          marker.content = document.querySelector(`#${marker.dataset.markerContent}`);
          const options = JSON.parse(marker.dataset.colors);

          if (marker.content.tagName === 'IMG') {
            gsap.set(marker.content, {transformOrigin: 'center'});

            marker.content.enter = () => {
              this.updateIndex(marker.dataset.markerIndex);

              gsap.fromTo(
                marker.content,
                {autoAlpha: 0, rotateY: -30},
                {duration: 0.3, autoAlpha: 1, rotateY: 0}
              );

              gsap.to('.features-special-a', {backgroundColor: options.bg});
              gsap.to('.shape__special--1', {fill: options.primary});
              gsap.to('.shape__special--2', {fill: options.secondary});
            };
          }

          marker.content.leave = function() {
            gsap.to(marker.content, {duration: 0.3, autoAlpha: 0});
          };
        });
      },
    });
  }

  setIndex(contentMarkers) {
    let markerIndex = null;
    let firstMatch = false;

    contentMarkers.forEach((marker, index) => {
      if (!firstMatch && isInView(marker)) {
        firstMatch = true;
        markerIndex = index + 1;

        gsap.to(document.querySelector('#img1'), {autoAlpha: 0});
        gsap.to(document.querySelector(`#img${markerIndex}`), {autoAlpha: 1});
      }
    });

    if (markerIndex) {
      this.updateIndex(markerIndex);
    } else {
      this.currentIndex = 1;
      this.nextIndex = 2;
    }

    this.lastContent = document.querySelector(
      `[data-marker-content="img${this.currentIndex}"]`
    );
  }

  updateIndex(index) {
    index = parseInt(index, 10);
    this.currentIndex = index;

    if (index !== this.maxLength) {
      this.nextIndex = index + 1;
    }

    if (index !== 1) {
      this.previousIndex = index - 1;
    }
  }

  /**
   * @param {object} args arguments object from ScrollTrigger
   */
  getCurrentSection(args) {
    const currScroll = scrollY;
    let newContent;

    if (args.isActive) {
      gsap.to('.shape__special--1', {translateY: 0});
      gsap.to('.shape__special--2', {translateX: 0});
    } else {
      gsap.to('.shape__special--1', {translateY: '-100%'});
      gsap.to('.shape__special--2', {translateX: '100%'});
    }

    if (args.direction === 1) {
      const next = document.querySelector(
        `[data-marker-content="img${this.nextIndex}"]`
      );
      if (next) {
        const top =
          next.getBoundingClientRect().top + document.documentElement.scrollTop;

        if (currScroll > top - 100) {
          newContent = next;
        }
      }
    } else {
      const next = document.querySelector(
        `[data-marker-content="img${this.previousIndex}"]`
      );

      if (next) {
        const top =
          next.getBoundingClientRect().top + document.documentElement.scrollTop;
        const bottom =
          top + next.querySelector('[data-element="pin-block"]').offsetHeight;

        if (currScroll < bottom + 100) {
          newContent = next;
        }
      }
    }

    if (newContent && !newContent.isSameNode(this.lastContent)) {
      if (this.lastContent) {
        this.lastContent.content.leave();
      }
      newContent.content.enter();
      this.lastContent = newContent;
    }
  }
}

const specialFeatureA = {
  init() {
    const specialFeature = document.querySelector('[data-module="features-special-a"]');
    if (specialFeature) {
      const feature = new SpecialFeatureA();
      feature.init();
    }
  },
};

export default specialFeatureA;
